// =============================================================================
//  Copyright : 2022 semantics GmbH
//  Date      : $Date: 2025-11-07 15:45:48 +0100 (Fri, 07 Nov 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 157914 $
//  Purpose   : low level components on db tree data
// =============================================================================
// ---Third-party imports
import React from 'react'
import clsx from 'clsx'

// ---Framework imports
import {callIfFunc} from 'ldash'
import {Actions} from 'uix/registries'
import log from 'uix/logging'
import {TreeNode} from 'uix/types/components'
import {useState, useMemo} from 'uix/hooks'
import {hashObj} from 'uix/utils'

// ---Local imports
import {useTreeNodes, useTreeNode} from './hooks'
import styles from './styles.scss'

// ---Globals
export const TreeNodeItem = ({otid, unloaded = '...'}) => {
  /* TreeNode with data loading */
  let treeNodeInfo = useTreeNode(otid)
  return treeNodeInfo ? <TreeNode node={treeNodeInfo} /> : unloaded
}

export const TreeNodeList = ({
  otids, // [1, 2, ...]
  nodes, // allow duplicate otids [{otid: 1, nkey: 1_x}, {otid: 2, nkey: 2_y}]
  selected: outerSelected,
  onSelect,
  annotate, // build custom annotation (right floated)
  style,
  link,
  linkLazy,
}) => {
  otids = useMemo(() => (nodes ? nodes.map(n => n.otid) : otids), [hashObj(otids), nodes])

  if (!nodes) {
    nodes = otids.map(otid => ({otid}))
  }

  let data = useTreeNodes(otids)
  let [selected, setSelected] = useState(null)
  selected = outerSelected === undefined ? selected : outerSelected

  let selectable = !!onSelect
  let onClick = nkey => {
    setSelected(nkey)
    callIfFunc(onSelect, nkey)
  }
  let renderedNodes = nodes
    .filter(({otid}) => data[otid])
    .map(({otid, nkey}) => {
      nkey = nkey || otid
      return (
        <div
          key={nkey}
          style={style}
          className={clsx(
            styles.treeListItem,
            selectable && styles.selectable,
            nkey == selected && styles.selected
          )}
          onClick={selectable ? () => onClick(nkey) : null}
        >
          {annotate ? <span style={{float: 'right'}}>{annotate({otid, nkey})}</span> : null}
          <TreeNode node={data[otid]} link={link} linkLazy={linkLazy} />
        </div>
      )
    })
  return <div className={styles.treeNodeList}>{renderedNodes}</div>
}

export const UnresolvedTreeNode = ({otid, placeholder = '...', ...props}) => {
  let node = useTreeNode(otid)
  return node ? <TreeNode node={node} {...props} /> : placeholder
}
